import { createContext } from "react";
import { getRouteApi } from "@tanstack/react-router";
import {
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  QueryObserverSuccessResult,
  UseQueryResult,
  UseSuspenseQueryResult,
  useMutation,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  CompositeFilterDescriptor,
  DataResult,
  FilterDescriptor,
  State,
  toODataString,
} from "@progress/kendo-data-query";
import { rewriteDateEqualsFilter } from "@/components/shared/tables/KendoTableClientState/kendo-filter-utils";

import {
  ChildDataModel,
  LeaModel,
  LeaDataModel,
  defaultLeaData,
  CoreDataModel,
  ServerError,
  GlobalStateModel,
  PreschoolModel,
  SecurityRole,
  TypedDataResult,
  ReportItem,
  SystemMessage,
} from "@/models.ts";
import { CHLD, CHLD_Key, CHLD_TEMPLATE, updateCHLD } from "@/models/chld.tsx";
import { FAMI, FAMI_Key, FAMI_TEMPLATE } from "@/models/fami.tsx";
import {
  CLRM,
  CLRM_Key,
  Parse_CLRM_Key,
  CLRM_TEMPLATE,
  updateCLRM,
} from "@/models/clrm.tsx";
import { STFF, STAFF_Key, STFF_TEMPLATE, updateSTFF } from "@/models/stff";
import { User, UserOrg } from "@/models/user.tsx";
import {
  fetchAuthenticatedData,
  useAuthenticatedSuspenseQuery,
} from "@/queries/shared";
import { addPlaceholderEntities, groupEntities } from "@/utils/util";
import { ExtractRequest } from "@/models/extract.tsx";
import {
  AnomalyProcess,
  AnomalyRun,
  AnomalyRunStats,
  CandidateValidationError,
  Certification,
  CertificationCycleStatusRow,
  CertificationLog,
  CertificationStats,
  FileStatusEnum,
  FileSubmission,
  FileSubmissionState,
  ValidationError,
} from "@/models/file-submission.tsx";
import { CAPSDACFeatureFlags } from "@/models/feature-flag.ts";
import { CSPPIDMatch, CSPPIDMatchLEA } from "@/models/mid.tsx";

const rootRoute = getRouteApi("__root__");
const leaRoute = getRouteApi("/$leaid");

export const coreDataQueryOptions = (globalState: GlobalStateModel) => {
  return queryOptions({
    queryKey: ["core"],
    queryFn: () =>
      // skipVersionCheck is true on URLs where the ResponseCache attribute is set on the
      // server response, since the version in the HTTP response may be from an old response
      fetchAuthenticatedData<CoreDataModel>(
        globalState,
        "/json/core",
        undefined,
        true
      ),
    // Set a 1-hour cache for static data
    staleTime: 30 * 60 * 1000,
  });
};

export const preschoolDataQueryOptions = (
  globalState: GlobalStateModel,
  esiidPath: string
) => {
  return queryOptions({
    queryKey: ["lea_preschools", esiidPath],
    queryFn: () =>
      fetchAuthenticatedData<PreschoolModel[]>(
        globalState,
        `/json/entity/${esiidPath}/preschools`,
        // skipVersionCheck is true on URLs where the ResponseCache attribute is set on the
        // server response, since the version in the HTTP response may be from an old response
        undefined,
        true
      ),
    // Set the stale time at an hour since preschools are unexpected to change
    staleTime: 3600 * 1000,
  });
};

export function useCoreData(): CoreDataModel {
  const globalState = rootRoute.useRouteContext();
  return globalState.coreData;
}

export function hasRoleFn(
  securityRoles: SecurityRole[],
  userOrg: UserOrg | undefined
) {
  return function (...roles: string[]) {
    // If we haven't been able to determine the current org yet, return undefined since we do not want to outright reject access yet.
    if (userOrg === undefined || securityRoles === undefined) return undefined;
    const currentRoles = userOrg.roleIds.map(
      (x) => securityRoles.find((r) => r.CAPSDACRoleId === x)?.CAPSDACRoleName
    );
    for (let role of roles) {
      if (currentRoles?.find((r) => r === role) !== undefined) {
        return true;
      }
    }
    return false;
  };
}

export function reprocessLea(
  hasRole: (...roles: string[]) => boolean | undefined,
  lea_data: LeaDataModel
) {
  lea_data.chld.forEach((c) => updateCHLD(c, hasRole, lea_data));
  lea_data.stff.forEach((s) => updateSTFF(s, hasRole, lea_data));
  lea_data.clrm.forEach((cr) => updateCLRM(cr, hasRole, lea_data));
  lea_data.entities = {
    children: addPlaceholderEntities<CHLD>(
      groupEntities<CHLD>(CHLD_Key, lea_data.chld),
      [
        ...lea_data.fami.map((x) => x.CSPPID),
        ...lea_data.enrl.map((x) => x.CSPPID),
        ...lea_data.clen.map((x) => x.CSPPID),
        ...lea_data.attn.map((x) => x.CSPPID),
        ...lea_data.lang.map((x) => x.CSPPID),
        ...lea_data.slng.map((x) => x.CSPPID),
      ],
      (id) =>
        updateCHLD({ ...CHLD_TEMPLATE, ...{ CSPPID: id } }, hasRole, lea_data)
    ),
    families: groupEntities<FAMI>(FAMI_Key, lea_data.fami),
    classrooms: addPlaceholderEntities<CLRM>(
      groupEntities<CLRM>(CLRM_Key, lea_data.clrm),
      [
        ...lea_data.sclr.map((x) => CLRM_Key(x)),
        ...lea_data.clen.map((x) => CLRM_Key(x)),
        ...lea_data.attn.map((x) => CLRM_Key(x)),
      ],
      (id) =>
        updateCLRM(
          { ...CLRM_TEMPLATE, ...Parse_CLRM_Key(id) },
          hasRole,
          lea_data
        )
    ),
    staff: addPlaceholderEntities<STFF>(
      groupEntities<STFF>(STAFF_Key, lea_data.stff),
      [
        ...lea_data.sclr.map((x) => x.LocalStaffId),
        ...lea_data.sass.map((x) => x.LocalStaffId),
      ],
      (id) =>
        updateSTFF(
          { ...STFF_TEMPLATE, ...{ LocalStaffId: id } },
          hasRole,
          lea_data
        )
    ),
  };
}

export function useLeaData(
  esiidPath: string
): UseSuspenseQueryResult<LeaDataModel> {
  const globalState = rootRoute.useLoaderData();
  const url = `/json/entity/${esiidPath}/data`;
  const loginLeas = globalState.loginDetail.userOrgs;
  const lea = useSuspenseQuery<LeaDataModel>({
    queryKey: ["leaData", esiidPath],
    queryFn: () => {
      const leaData = fetchAuthenticatedData<LeaDataModel>(globalState, url);
      const currentUserOrg = loginLeas.find((x) => x.esiIdPath === esiidPath);
      const hasRole = hasRoleFn(
        globalState.coreData.securityRoles,
        currentUserOrg
      );
      return leaData.then((lea_data) => {
        reprocessLea(hasRole, lea_data);
        return lea_data;
      });
    },
    //enabled: !!(core.data?.countryList),
    // Given the noticeable delay in loading large amounts of data, set the stale time at 5 minutes since we manually
    // invalidate the cache when data is changed in this client.
    staleTime: 300 * 1000,
  });
  return lea;
}

export function invalidateLeaData(queryClient: QueryClient, esiidPath: string) {
  queryClient.resetQueries({ queryKey: ["leaData", esiidPath] });
  // Side-effect of forcing a cache refresh
  //const leaData = getLeaData(leaid)
}

export function useChildData(
  csppid: string | undefined
): UseSuspenseQueryResult<ChildDataModel> {
  const globalState = rootRoute.useLoaderData();
  const childData = useSuspenseQuery<ChildDataModel>({
    queryKey: ["csppid", csppid],
    queryFn: () => {
      if (typeof csppid === "undefined")
        return Promise.resolve({} as ChildDataModel);
      return fetchAuthenticatedData<ChildDataModel>(
        globalState,
        `/json/child/${csppid}/detail`
      ).then((child_data) => {
        child_data.fami.forEach((f: FAMI) => (f.Enrollments = []));
        child_data.chld.forEach((c: CHLD) => {
          c.FamilyRecords = child_data.fami;
          c.Enrollments = [];
          c.ClassroomAssignments = [];
          c.LangRecords = [];
          c.SLngRecords = [];
          c.Attendance = [];
        });
        return child_data;
      });
    },
  });
  return childData;
}

export function getSubmissionsQueryKey(leaid: string): string[] {
  return ["filesubmissions", leaid];
}

export function getCandidateSubmissionsQueryKey(leaid: string): string[] {
  return ["candidatesubmissions", leaid];
}

export function useFileSubmissionsData(
  esiidPath: string,
  refetchInterval: number | false = false,
  enabled: boolean = true
): UseQueryResult<FileSubmission[]> {
  const globalState = rootRoute.useLoaderData();
  const submissions = useQuery<FileSubmission[]>({
    queryKey: getSubmissionsQueryKey(esiidPath),
    queryFn: () =>
      fetchAuthenticatedData<any>(
        globalState,
        `/json/filesubmissions/${esiidPath}`
      ).then((x) => x.records),
    refetchInterval: refetchInterval,
    enabled: enabled,
  });
  return submissions;
}

// Same function as getFileSubmissionsData, except updated for useSuspenseQuery, and renamed to pass eslint(react-hooks/rules-of-hooks)
export function useFileSubmissionsSuspenseQuery(
  leaid: string,
  refetchInterval: number | false = false
): UseSuspenseQueryResult<FileSubmission[]> {
  const globalState = rootRoute.useLoaderData();
  const submissions = useSuspenseQuery<FileSubmission[]>({
    queryKey: getSubmissionsQueryKey(leaid),
    queryFn: () =>
      fetchAuthenticatedData<any>(
        globalState,
        `/json/filesubmissions/${leaid}`
      ).then((x) => x.records),
    refetchInterval: refetchInterval,
  });
  return submissions;
}

export function useExtractsSuspenseQuery(
  leaid: string,
  state: State,
  refetchInterval: number | false = false
): UseSuspenseQueryResult<TypedDataResult<ExtractRequest>> {
  const globalState = rootRoute.useLoaderData();
  const extracts = useSuspenseQuery<DataResult>({
    queryKey: ["extracts", leaid, state],
    queryFn: () => {
      return fetchAuthenticatedData<any>(
        globalState,
        `json/odata/extracts?esiidPath=${leaid}&$count=true&${toODataString(state)}`
      ).then((json) => ({ data: json.value, total: json["@odata.count"] }));
    },
    refetchInterval: refetchInterval,
  });
  return extracts;
}

export function useExtractsQuery(
  leaid: string,
  state: State,
  refetchInterval: number | false = false
): UseQueryResult<TypedDataResult<ExtractRequest>> {
  const globalState = rootRoute.useLoaderData();
  const extracts = useQuery<DataResult>({
    queryKey: ["extracts", leaid, state],
    queryFn: () => {
      const excludeStateLevelFilter: FilterDescriptor = {
        field: "ExtractType",
        operator: "neq",
        value: "State Enrollment",
      };
      const odataState: State = {
        ...state,
        filter: {
          logic: "and",
          filters: state.filter
            ? [
                excludeStateLevelFilter,
                rewriteDateEqualsFilter(state.filter),
              ]
            : [excludeStateLevelFilter],
        },
      };
      return fetchAuthenticatedData<any>(
        globalState,
        `json/odata/extracts?esiidPath=${leaid}&$count=true&${toODataString(odataState)}`
      ).then((json) => ({ data: json.value, total: json["@odata.count"] }));
    },
    // queryKey includes filter/sort/page state, so every change is
    // technically a fresh, uncached query - without this, `data` would
    // briefly go undefined on every one of those changes (including every
    // debounced date-filter commit), showing "No items to show" for a
    // moment even though the grid isn't actually empty. This keeps the
    // previous rows visible until the new ones arrive instead.
    placeholderData: keepPreviousData,
    refetchInterval: refetchInterval,
  });
  return extracts;
}

export function useFileSubmissionErrors(
  esiidPath: string,
  filesubmissionid: string,
  enabled: boolean = true
): UseQueryResult<ValidationError<string>[]> {
  const globalState = rootRoute.useLoaderData();
  const errors = useQuery<ValidationError<string>[]>({
    queryKey: ["validation_errors", esiidPath, filesubmissionid],
    queryFn: () => {
      if (!esiidPath || !filesubmissionid) {
        return Promise.resolve([]);
      }
      return fetchAuthenticatedData<any>(
        globalState,
        `/json/filesubmission/${esiidPath}/${filesubmissionid}`
      ).then((x) => x.errors);
    },
    // Set a 1-hour cache for static data
    staleTime: 30 * 60 * 1000,
    enabled: enabled,
  });
  return errors;
}

export function useCandidateSubmissionErrors(
  esiidPath: string,
  filesubmissionid: string,
  enabled: boolean = true
): UseQueryResult<CandidateValidationError<string>[]> {
  const globalState = rootRoute.useLoaderData();
  const errors = useQuery<ValidationError<string>[]>({
    queryKey: ["validation_errors", esiidPath, filesubmissionid],
    queryFn: () => {
      if (!esiidPath || !filesubmissionid) {
        return Promise.resolve([]);
      }
      return fetchAuthenticatedData<any>(
        globalState,
        `/json/candidatesubmission/${esiidPath}/${filesubmissionid}`
      ).then((x) => x.errors);
    },
    // Set a 1-hour cache for static data
    staleTime: 30 * 60 * 1000,
    enabled: enabled,
  });
  return errors;
}

export function useEntity(esiidPath: string): UseSuspenseQueryResult<LeaModel> {
  const globalState = rootRoute.useLoaderData();
  const entity = useSuspenseQuery<LeaModel>({
    queryKey: ["entity", esiidPath],
    queryFn: () =>
      fetchAuthenticatedData<LeaModel>(
        globalState,
        `/json/entity/${esiidPath}`
      ),
    // Set a 1-hour cache for static data
    staleTime: 30 * 60 * 1000,
  });
  return entity;
}

export function useChildEntities(
  esiidPath: string
): UseSuspenseQueryResult<LeaModel[]> {
  const globalState = rootRoute.useLoaderData();
  const entities = useSuspenseQuery<LeaModel[]>({
    queryKey: ["child_entities", esiidPath],
    queryFn: () =>
      fetchAuthenticatedData<LeaModel[]>(
        globalState,
        `/json/entity/${esiidPath}/children`
      ),
    // Set a 1-hour cache for static data
    staleTime: 30 * 60 * 1000,
  });
  return entities;
}

export const enabledFeaturesQueryOptions = (globalState: GlobalStateModel) => {
  return queryOptions({
    queryKey: ["features"],
    queryFn: () =>
      fetchAuthenticatedData<string[]>(globalState, `/json/identity/features`),
    // Set a 1-hour cache for static data
    staleTime: 30 * 60 * 1000,
  });
};

const ADMIN_FEATURE_FLAGS_KEY = "admin-feature-flags";

export const featureFlagsQueryOptions = (globalState: GlobalStateModel) => {
  return queryOptions({
    queryKey: [ADMIN_FEATURE_FLAGS_KEY],
    queryFn: () =>
      fetchAuthenticatedData<CAPSDACFeatureFlags[]>(
        globalState,
        `/json/feature/all`
      ),
  });
};

export function useFeatureFlags(): UseQueryResult<CAPSDACFeatureFlags[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery(featureFlagsQueryOptions(globalState));
}

export function useUpdateFeatureFlag() {
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();
  return useMutation<CAPSDACFeatureFlags, Error, CAPSDACFeatureFlags>({
    mutationFn: (featureFlag) =>
      fetchAuthenticatedData<CAPSDACFeatureFlags>(
        globalState,
        `/json/feature/update`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(featureFlag),
        }
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEATURE_FLAGS_KEY] }),
  });
}

export const loginDataQueryOptions = (globalState: GlobalStateModel) => {
  return queryOptions({
    queryKey: ["login_detail"],
    queryFn: () =>
      fetchAuthenticatedData<User<UserOrg>>(
        globalState,
        `/json/identity/profile`
      ),
  });
};

export function useLoginDetail(): User<UserOrg> {
  const globalState = rootRoute.useLoaderData();
  return globalState.loginDetail;
}

export function useUsers(
  leaid: string,
  enabled: boolean = true
): UseQueryResult<User<UserOrg>[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<User<UserOrg>[]>({
    queryKey: ["users", leaid],
    queryFn: () =>
      fetchAuthenticatedData<User<UserOrg>[]>(
        globalState,
        `/json/account/${leaid}/users`
      ),
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useUserDetail(
  leaid: string,
  userid: string
): UseQueryResult<User<UserOrg>> {
  const globalState = rootRoute.useLoaderData();
  const user = useQuery<User<UserOrg>>({
    queryKey: ["user", leaid, userid],
    queryFn: () =>
      fetchAuthenticatedData<User<UserOrg>>(
        globalState,
        `/json/account/${leaid}/users/${userid}`
      ),
    enabled: !!userid,
  });
  return user;
}

export function useCertifications(
  leaid: string,
  enabled: boolean = true
): UseQueryResult<Certification[]> {
  const globalState = rootRoute.useLoaderData();
  const certifications = useQuery<Certification[]>({
    queryKey: ["certifications", leaid],
    queryFn: () =>
      fetchAuthenticatedData<Certification[]>(
        globalState,
        `/json/certification/${leaid}`
      ),
    enabled,
  });
  return certifications;
}

export function useCertification(
  leaid: string,
  certificationId: string,
  enabled: boolean = true
): UseQueryResult<Certification> {
  const globalState = rootRoute.useLoaderData();
  const certification = useQuery<Certification>({
    queryKey: ["certifications", leaid, certificationId],
    queryFn: () =>
      fetchAuthenticatedData<Certification>(
        globalState,
        `/json/certification/${leaid}/${certificationId}`
      ),
    enabled,
  });
  return certification;
}

export function useCertificationLogs(
  esiidPath: string,
  certificationId: string
): UseQueryResult<CertificationLog[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<CertificationLog[]>({
    queryKey: ["certification_logs", esiidPath, certificationId],
    queryFn: () => {
      return fetchAuthenticatedData<CertificationLog[]>(
        globalState,
        `/json/certificationlogs/certification/${esiidPath}/${certificationId}`
      ).then((data) => {
        return data;
      });
    },
    enabled: !!esiidPath && !!certificationId,
  });
}

export function useCertificationLog(
  esiidPath: string,
  certificationLogId: string
): UseQueryResult<CertificationLog> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<CertificationLog>({
    queryKey: ["certification_log", esiidPath, certificationLogId],
    queryFn: () =>
      fetchAuthenticatedData<CertificationLog>(
        globalState,
        `/json/certificationlogs/${esiidPath}/${certificationLogId}`
      ),
    enabled: !!esiidPath && !!certificationLogId,
  });
}

export function useCSPPIDMatches(
  leaid: string
): UseSuspenseQueryResult<CSPPIDMatchLEA[], Error> {
  return useAuthenticatedSuspenseQuery<CSPPIDMatchLEA[]>(
    `/json/csppid_match/${leaid}`,
    ["matches", leaid]
  );
}

export function useCertificationStats(
  esiidPath: string,
  certificationId: string
): UseQueryResult<CertificationStats> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<CertificationStats>({
    queryKey: ["certification_stats", esiidPath, certificationId],
    queryFn: () =>
      fetchAuthenticatedData<CertificationStats>(
        globalState,
        `/json/certification/${esiidPath}/${certificationId}/stats`
      ),
    enabled: !!esiidPath && !!certificationId,
  });
}

export function useCertificationCycleStatus(
  reportingCalendarId: string,
  enabled: boolean = true
): UseQueryResult<CertificationCycleStatusRow[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<CertificationCycleStatusRow[]>({
    queryKey: ["certification_cycle_status", reportingCalendarId],
    queryFn: () =>
      fetchAuthenticatedData<CertificationCycleStatusRow[]>(
        globalState,
        `/json/state/certification/cycle_status/${reportingCalendarId}`
      ),
    enabled: enabled && !!reportingCalendarId,
  });
}

export function useAnomalyRunStats(
  esiidPath: string,
  certificationId: string,
  anomalyRunId: string
): UseQueryResult<AnomalyRunStats> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<AnomalyRunStats>({
    queryKey: ["anomaly_run_stats", esiidPath, certificationId, anomalyRunId],
    queryFn: () =>
      fetchAuthenticatedData<AnomalyRunStats>(
        globalState,
        `/json/certification/${esiidPath}/${certificationId}/anomaly_run/${anomalyRunId}/stats`
      ),
    enabled: !!esiidPath && !!certificationId && !!anomalyRunId,
  });
}

const ANOMALY_PROCESSES_KEY = "anomaly-processes";

export const anomalyProcessesQueryOptions = (globalState: GlobalStateModel) => {
  return queryOptions({
    queryKey: [ANOMALY_PROCESSES_KEY],
    queryFn: () =>
      fetchAuthenticatedData<AnomalyProcess[]>(
        globalState,
        `/json/state/anomaly_process/all`
      ),
  });
};

export function useAnomalyProcesses(): UseQueryResult<AnomalyProcess[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery(anomalyProcessesQueryOptions(globalState));
}

export function useActivateAnomalyProcess() {
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();
  return useMutation<AnomalyProcess, Error, number>({
    mutationFn: (anomalyProcessId) =>
      fetchAuthenticatedData<AnomalyProcess>(
        globalState,
        `/json/state/anomaly_process/${anomalyProcessId}/activate`,
        { method: "POST" }
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [ANOMALY_PROCESSES_KEY] }),
  });
}

export function useDeactivateAnomalyProcess() {
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();
  return useMutation<AnomalyProcess, Error, number>({
    mutationFn: (anomalyProcessId) =>
      fetchAuthenticatedData<AnomalyProcess>(
        globalState,
        `/json/state/anomaly_process/${anomalyProcessId}/deactivate`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(""),
        }
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [ANOMALY_PROCESSES_KEY] }),
  });
}

export function getCSPPIDMatch(
  leaid: string,
  matchId: string
): UseQueryResult<CSPPIDMatch> {
  const globalState = rootRoute.useLoaderData();
  const match = useQuery<CSPPIDMatch>({
    queryKey: ["match", leaid, matchId],
    queryFn: () =>
      fetchAuthenticatedData<CSPPIDMatch>(
        globalState,
        `/json/csppid_match/${leaid}/${matchId}`
      ),
  });
  return match;
}

export function useFileSubmissionState(
  esiidPath: string,
  fileSubmission: FileSubmission | undefined
): FileSubmissionState {
  var state: FileSubmissionState = {
    currentStatus: undefined,
    files: undefined,
    errors: undefined,
  };
  var submissionData = useFileSubmissionsData(
    esiidPath,
    2000 /* refresh every 2 seconds */,
    fileSubmission != undefined
  );
  if (fileSubmission != undefined && submissionData.data != undefined) {
    state.files = submissionData.data.filter(
      (x) =>
        x.Id === fileSubmission.Id ||
        ![
          FileStatusEnum.Posted,
          FileStatusEnum.Rejected,
          FileStatusEnum.Failed,
        ].includes(x.CurrentStatus)
    );
    state.currentStatus = state.files.find(
      (x) => x.Id === fileSubmission.Id
    )?.CurrentStatus;
  }
  var fileSubmissionError =
    state.currentStatus === FileStatusEnum.Rejected ||
    state.currentStatus === FileStatusEnum.Failed;
  state.errors = useFileSubmissionErrors(
    esiidPath,
    fileSubmission?.Id,
    fileSubmissionError
  ).data;
  return state;
}

export function useMonthlyOnDemandReports(
  leaid: string
): UseQueryResult<ReportItem[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<ReportItem[]>({
    queryKey: ["monthly_reports", leaid],
    queryFn: () =>
      fetchAuthenticatedData<ReportItem[]>(
        globalState,
        `/ReportDirectory/${leaid}/ondemand/monthly`
      ),
    enabled: !!leaid,
  });
}

export function useMonthlyCertificationReports(
  leaid: string
): UseQueryResult<ReportItem[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery<ReportItem[]>({
    queryKey: ["monthly_certification_reports", leaid],
    queryFn: () =>
      fetchAuthenticatedData<ReportItem[]>(
        globalState,
        `/ReportDirectory/${leaid}/certification/monthly`
      ),
    enabled: !!leaid,
  });
}

// =============================================================================
// System Messages
// =============================================================================

export interface DashboardSystemMessage {
  title: string;
  messageBody: string;
  labelCode: string;
}

const SYSTEM_MESSAGES_KEY = "system_messages";
const DASHBOARD_SYSTEM_MESSAGES_KEY = "dashboard_system_messages";

// ── Admin list ────────────────────────────────────────────────────────────

export const systemMessagesQueryOptions = (
  globalState: GlobalStateModel,
  includePastMessages: boolean = false,
  includeInactive: boolean = false
) => {
  const params = new URLSearchParams();
  if (includePastMessages) params.set("includePastMessages", "true");
  if (includeInactive) params.set("includeInactive", "true");
  const qs = params.toString() ? `?${params.toString()}` : "";
  return queryOptions({
    queryKey: [SYSTEM_MESSAGES_KEY, includePastMessages, includeInactive],
    queryFn: () =>
      fetchAuthenticatedData<SystemMessage[]>(
        globalState,
        `/json/systemmessages/admin${qs}`
      ),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export function useSystemMessages(
  includePastMessages: boolean = false,
  includeInactive: boolean = false
): UseQueryResult<SystemMessage[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery(
    systemMessagesQueryOptions(
      globalState,
      includePastMessages,
      includeInactive
    )
  );
}

export function invalidateSystemMessages(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: [SYSTEM_MESSAGES_KEY] });
  queryClient.invalidateQueries({ queryKey: [DASHBOARD_SYSTEM_MESSAGES_KEY] });
}

export const dashboardSystemMessagesQueryOptions = (
  globalState: GlobalStateModel,
  reportingLEA: string
) => {
  return queryOptions({
    queryKey: [DASHBOARD_SYSTEM_MESSAGES_KEY, reportingLEA],
    queryFn: () =>
      fetchAuthenticatedData<DashboardSystemMessage[]>(
        globalState,
        `/json/systemmessages/lea/${reportingLEA}`
      ),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
};

export function useDashboardSystemMessages(
  reportingLEA: string
): UseQueryResult<DashboardSystemMessage[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery(
    dashboardSystemMessagesQueryOptions(globalState, reportingLEA)
  );
}

export function useAddSystemMessage() {
  const globalState = rootRoute.useLoaderData();
  const { leaid } = leaRoute.useParams();
  const queryClient = useQueryClient();
  return useMutation<SystemMessage, Error, SystemMessage>({
    mutationFn: (message) => {
      if (!leaid) {
        return Promise.reject(
          new Error("Cannot create system message: no organization in route")
        );
      }
      return fetchAuthenticatedData<SystemMessage>(
        globalState,
        `/json/systemmessages/add?esiidPath=${encodeURIComponent(leaid)}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(message),
        }
      );
    },
    onSuccess: () => invalidateSystemMessages(queryClient),
  });
}

export function useEditSystemMessage() {
  const globalState = rootRoute.useLoaderData();
  const { leaid } = leaRoute.useParams();
  const queryClient = useQueryClient();
  return useMutation<SystemMessage, Error, SystemMessage>({
    mutationFn: (message) => {
      if (!leaid) {
        return Promise.reject(
          new Error("Cannot edit system message: no organization in route")
        );
      }
      return fetchAuthenticatedData<SystemMessage>(
        globalState,
        `/json/systemmessages/edit?esiidPath=${encodeURIComponent(leaid)}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(message),
        }
      );
    },
    onSuccess: () => invalidateSystemMessages(queryClient),
  });
}
export function useDeleteSystemMessage() {
  const globalState = rootRoute.useLoaderData();
  const { leaid } = leaRoute.useParams();
  const queryClient = useQueryClient();
  return useMutation<void, Error, SystemMessage>({
    mutationFn: (message) => {
      if (!leaid) {
        return Promise.reject(
          new Error("Cannot delete system message: no organization in route")
        );
      }
      return fetchAuthenticatedData<void>(
        globalState,
        `/json/systemmessages/delete?esiidPath=${encodeURIComponent(leaid)}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(message),
        }
      );
    },
    onSuccess: () => invalidateSystemMessages(queryClient),
  });
}

export function useRestoreSystemMessage() {
  const globalState = rootRoute.useLoaderData();
  const { leaid } = leaRoute.useParams();
  const queryClient = useQueryClient();
  return useMutation<SystemMessage, Error, SystemMessage>({
    mutationFn: (message) => {
      if (!leaid) {
        return Promise.reject(
          new Error("Cannot restore system message: no organization in route")
        );
      }
      return fetchAuthenticatedData<SystemMessage>(
        globalState,
        `/json/systemmessages/restore?esiidPath=${encodeURIComponent(leaid)}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(message),
        }
      );
    },
    onSuccess: () => invalidateSystemMessages(queryClient),
  });
}
